import React, { useMemo, useState, useCallback } from "react";

export default function RatingTable({
  rows = [],
  include = "activity",
  onRowClick,
}) {
  const [sortKey, setSortKey] = useState("percentage");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const fmt = (n, d = 2) =>
    n === null || n === undefined || Number.isNaN(n)
      ? "—"
      : Number(n).toFixed(d);

  const toggleSort = useCallback(
    (key) => {
      if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      else {
        setSortKey(key);
        setSortDir("desc");
      }
    },
    [sortKey]
  );

  const sorted = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [rows, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const visible = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  if (!rows || rows.length === 0)
    return <div className="spd__empty">No data.</div>;

  return (
    <div className="spd__tableWrap">
      <table className="spd__table">
        <thead>
          <tr>
            <Th
              title="Name"
              align="left"
              onClick={() => toggleSort("name")}
              active={sortKey === "name"}
              dir={sortDir}
            />
            <Th title="Role" align="left" />
            <Th
              title="% Score"
              align="right"
              onClick={() => toggleSort("percentage")}
              active={sortKey === "percentage"}
              dir={sortDir}
            />
            <Th
              title="Participation"
              align="right"
              onClick={() => toggleSort("participationRate")}
              active={sortKey === "participationRate"}
              dir={sortDir}
            />
            <Th
              title="Active Perf."
              align="right"
              onClick={() => toggleSort("activePerformance")}
              active={sortKey === "activePerformance"}
              dir={sortDir}
            />
            {include !== "none" && <Th title="Streak (d)" align="right" />}
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => (
            <tr
              key={r.userId || r._id || r.name}
              className="spd__tr"
              onClick={() => onRowClick?.(r)}
            >
              <td className="spd__td">{r.name ?? "—"}</td>
              <td className="spd__td">{r.role ?? "—"}</td>
              <td className="spd__td spd__td--right">{fmt(r.percentage)}</td>
              <td className="spd__td spd__td--right">
                {fmt(r.participationRate)}
              </td>
              <td className="spd__td spd__td--right spd__td--strong">
                {fmt(r.activePerformance)}
              </td>
              {include !== "none" && (
                <td className="spd__td spd__td--right">
                  {r?.activity?.streakDays ?? "—"}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination
        page={page}
        totalPages={totalPages}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </div>
  );
}

function Th({ title, align = "left", onClick, active, dir }) {
  return (
    <th
      className={`spd__th spd__th--${align} ${active ? "spd__th--active" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <span>{title}</span>
      {active && <span className="spd__sort">{dir === "asc" ? "▲" : "▼"}</span>}
    </th>
  );
}

function Pagination({ page, totalPages, setPage, pageSize, setPageSize }) {
  return (
    <div className="spd__pager">
      <div className="spd__pagerLeft">
        <button
          className="spd__btn spd__btn--ghost"
          onClick={() => setPage(1)}
          disabled={page === 1}
        >
          ⏮ First
        </button>
        <button
          className="spd__btn spd__btn--ghost"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          ‹ Prev
        </button>
        <span className="spd__hint">
          Page {page} / {totalPages}
        </span>
        <button
          className="spd__btn spd__btn--ghost"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
        >
          Next ›
        </button>
        <button
          className="spd__btn spd__btn--ghost"
          onClick={() => setPage(totalPages)}
          disabled={page === totalPages}
        >
          Last ⏭
        </button>
      </div>
      <div className="spd__pagerRight">
        <label className="spd__label" htmlFor="rows">
          Rows
        </label>
        <select
          id="rows"
          className="spd__select spd__select--xs"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
